import { useEffect, useRef, useState } from 'react'
import type { LocalTrack, Room } from 'livekit-client'
import { useLivekitStore } from '../stores/livekitStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { getSharedAudioContext } from '../components/voice/audioContext'

/**
 * Read the local participant's audio level (0..100).
 *
 * After several rounds of "pick one strategy" failing in production-Electron,
 * this version runs three strategies in parallel and returns the max:
 *
 *   1. **AnalyserNode on LiveKit's published track** — Web Audio reads the
 *      same `MediaStreamTrack` LiveKit is publishing. No second `getUserMedia`,
 *      no track clone — just a `createMediaStreamSource` over the existing
 *      track wrapped in a fresh MediaStream container. Most reliable when it
 *      works; in some Chromium configs the encoder pipeline starves the
 *      source so the analyser sees zero.
 *   2. **RTCRtpSender stats** — `localAudioTrack.getRTCStatsReport()` exposes
 *      WebRTC `media-source.audioLevel` which the browser fills regardless
 *      of any Web Audio routing. Lower frequency (~1-2Hz) but standards-
 *      based.
 *   3. **LiveKit `localParticipant.audioLevel`** — sparse but populated by
 *      the SDK on active-speaker events. Last-resort.
 *
 * Set `window.__rxDebugAudioMeter = true` in the DevTools console to see
 * a 1Hz sample of every strategy alongside the final value — invaluable
 * when debugging "meter doesn't move" reports.
 */
export function useLocalAudioLevel(): number {
  const room = useLivekitStore((s) => s.room)
  const localAudioTrack = useLivekitStore((s) => s.localAudioTrack)
  const isAudioTrackReady = useLivekitStore((s) => s.isAudioTrackReady)
  const optimisticMicEnabled = useLivekitStore((s) => s.optimisticMicEnabled)
  const micOn = optimisticMicEnabled ?? isAudioTrackReady
  return useAudioLevelFor(room, localAudioTrack, micOn)
}

/**
 * Same telemetry pipeline, but pointed at the DM-call store instead of
 * the server voice channel store. Kept as a small wrapper so the DM
 * call surface can wire the AudioMeter the same way VoiceChannelView
 * does — without duplicating the analyser / RTC stats / fallback
 * machinery the moment the two paths drift.
 */
export function useDmLocalAudioLevel(): number {
  const room = useDmCallStore((s) => s.room)
  const localAudioTrack = useDmCallStore((s) => s.localAudioTrack)
  const isAudioTrackReady = useDmCallStore((s) => s.isAudioTrackReady)
  const isMuted = useDmCallStore((s) => s.isMuted)
  // dmCallStore doesn't carry the optimistic flag the voice store does;
  // the toggle path flips isMuted immediately, so isMuted=false plus a
  // ready track is the equivalent ground-truth.
  const micOn = isAudioTrackReady && !isMuted
  return useAudioLevelFor(room, localAudioTrack, micOn)
}

function useAudioLevelFor(room: Room | null, localAudioTrack: LocalTrack | undefined, micOn: boolean): number {
  const [level, setLevel] = useState(0)
  const smoothedRef = useRef(0)

  useEffect(() => {
    if (!localAudioTrack) {
      smoothedRef.current = 0
      setLevel(0)
      return
    }

    let cancelled = false
    let analyser: AnalyserNode | null = null
    let analyserSource: MediaStreamAudioSourceNode | null = null
    let analyserBuf: Float32Array | null = null
    let lastRtcLevel = 0
    let rtcTimer: ReturnType<typeof setTimeout> | null = null
    let debugTimer: ReturnType<typeof setTimeout> | null = null

    // ─── Strategy 1: AnalyserNode on LiveKit's track ───
    try {
      const track = (localAudioTrack as any).mediaStreamTrack as MediaStreamTrack | undefined
      if (track) {
        const ctx = getSharedAudioContext()
        if (ctx.state === 'suspended') ctx.resume().catch(() => {})
        const stream = new MediaStream([track])
        analyserSource = ctx.createMediaStreamSource(stream)
        analyser = ctx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.3
        analyserSource.connect(analyser)
        analyserBuf = new Float32Array(analyser.fftSize)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[useLocalAudioLevel] analyser setup failed:', err)
    }

    const readAnalyser = () => {
      if (!analyser || !analyserBuf) return 0
      // TS lib types insist on Float32Array<ArrayBuffer> here, but our buf
      // is ArrayBufferLike — cast to keep both DOM lib variants happy.
      analyser.getFloatTimeDomainData(analyserBuf as unknown as Float32Array<ArrayBuffer>)
      let sum = 0
      for (let i = 0; i < analyserBuf.length; i++) sum += analyserBuf[i] * analyserBuf[i]
      const rms = Math.sqrt(sum / analyserBuf.length)
      return rms < 0.008 ? 0 : Math.min(100, rms * 100 * 5)
    }

    // ─── Strategy 2: RTCStats audioLevel poll (1Hz, low cost) ───
    const pollRtc = async () => {
      if (cancelled) return
      try {
        const stats = await (localAudioTrack as any).getRTCStatsReport?.()
        if (stats && typeof stats.forEach === 'function') {
          stats.forEach((stat: any) => {
            if (stat.type === 'media-source' && stat.kind === 'audio' && typeof stat.audioLevel === 'number') {
              lastRtcLevel = Math.min(100, stat.audioLevel * 100 * 5)
            }
          })
        }
      } catch { /* noop */ }
      if (!cancelled) rtcTimer = setTimeout(pollRtc, 500)
    }
    pollRtc()

    // ─── Combine — take the max each frame, smooth toward it ───
    const tick = () => {
      if (cancelled) return
      const fromAnalyser = readAnalyser()
      const fromLk = (room?.localParticipant?.audioLevel ?? 0) * 100 * 5
      const target = Math.min(100, Math.max(fromAnalyser, lastRtcLevel, fromLk))
      const lerp = target > smoothedRef.current ? 0.4 : 0.15
      smoothedRef.current = smoothedRef.current + (target - smoothedRef.current) * lerp
      const rounded = Math.round(smoothedRef.current)
      setLevel((prev) => (Math.abs(prev - rounded) >= 1 ? rounded : prev))
      requestAnimationFrame(tick)
    }
    const rafId = requestAnimationFrame(tick)

    // ─── Diagnostic dump (opt-in) ───
    if (typeof window !== 'undefined' && (window as any).__rxDebugAudioMeter) {
      const dump = () => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.debug('[useLocalAudioLevel]', {
          analyser: readAnalyser().toFixed(1),
          rtc: lastRtcLevel.toFixed(1),
          lk: ((room?.localParticipant?.audioLevel ?? 0) * 100 * 5).toFixed(1),
          smoothed: smoothedRef.current.toFixed(1),
        })
        debugTimer = setTimeout(dump, 1000)
      }
      dump()
    }

    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      if (rtcTimer) clearTimeout(rtcTimer)
      if (debugTimer) clearTimeout(debugTimer)
      try { analyserSource?.disconnect() } catch { /* noop */ }
      try { analyser?.disconnect() } catch { /* noop */ }
    }
  }, [localAudioTrack, room])

  return micOn ? level : 0
}
